import { cn, formatCompactNumber } from '@/lib/utils'
import type { ContextSection } from '@/types/contextObservability'
import { SECTION_COLORS } from '@/types/contextObservability'

export interface FlatViewProps {
  sections: ContextSection[]
  contextWindowSize: number
  className?: string
}

export function FlatView({ sections, contextWindowSize, className }: FlatViewProps) {
  const sorted = [...sections].sort((a, b) => b.tokens - a.tokens)
  const maxTokens = sorted[0]?.tokens ?? 1
  const totalUsed = sections.reduce((sum, s) => sum + s.tokens, 0)
  const totalPct = contextWindowSize > 0 ? (totalUsed / contextWindowSize) * 100 : 0

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium">Prompt composition</span>
        <span className="text-xs text-muted-foreground font-mono">
          {formatCompactNumber(totalUsed)} tok
        </span>
      </div>

      <div className="space-y-2">
        {sorted.map((section) => {
          const barPct = maxTokens > 0 ? (section.tokens / maxTokens) * 100 : 0
          const windowPct = contextWindowSize > 0
            ? (section.tokens / contextWindowSize) * 100
            : 0
          const color = SECTION_COLORS[section.key] ?? section.color

          return (
            <div key={section.key} className="grid grid-cols-[140px_1fr_auto] items-center gap-3">
              <div className="flex items-center gap-2 text-xs text-muted-foreground truncate">
                <span
                  className="h-2 w-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: color }}
                />
                <span className="truncate">{section.label}</span>
              </div>

              <div className="h-4 rounded bg-muted overflow-hidden">
                <div
                  className="h-full rounded transition-all"
                  style={{
                    width: `${barPct}%`,
                    backgroundColor: color,
                  }}
                />
              </div>

              <span className="text-xs font-mono text-muted-foreground w-24 text-right">
                {formatCompactNumber(section.tokens)} · {windowPct.toFixed(1)}%
              </span>
            </div>
          )
        })}
      </div>

      <div className="border-t pt-2 grid grid-cols-[140px_1fr_auto] items-center gap-3">
        <span className="text-xs font-medium">Total</span>
        <div className="h-4 rounded bg-muted overflow-hidden">
          <div
            className="h-full rounded bg-foreground/20 transition-all"
            style={{ width: `${Math.min(totalPct, 100)}%` }}
          />
        </div>
        <span className="text-xs font-mono font-medium w-24 text-right">
          {formatCompactNumber(totalUsed)} · {totalPct.toFixed(1)}%
        </span>
      </div>
    </div>
  )
}
